import aa from 'search-insights';
import { IOrderData } from '../models/IOrderData';
import { IAlgoliaSettings } from '../models/IAlgoliaSettings';
import getGoogleAnalyticsUserIdFromBrowserCookie from './algolia-user-token';
import Helpers from './helpers';

declare var algoliaSettings: IAlgoliaSettings;
declare var Cookiebot: any;
declare var dataLayer: any;

function searchAlgoliaSettings(key: string): string {
	if (typeof algoliaSettings === "undefined") {
		return null;
	}
	if (algoliaSettings[key]) {
		return algoliaSettings[key];
	}
	return "";
}

function setupTracking() {

	let userHasConsented = typeof Cookiebot !== "undefined" ? Cookiebot?.consent?.statistics : false;

	window.addEventListener('CookiebotOnConsentReady', function (e) {
		userHasConsented = Cookiebot.consent.statistics;
		initAlgoliaTracking();
	}, false);

	function initAlgoliaTracking() {
		aa("init", {
			appId: searchAlgoliaSettings('appId'),
			apiKey: searchAlgoliaSettings('apiKey')
		});

		aa('setUserToken', getGoogleAnalyticsUserIdFromBrowserCookie('_ga'));
		if (userHasConsented) {
			aa('setAuthenticatedUserToken', Helpers.searchDataLayer('customerId'));
		}
	}

	initAlgoliaTracking();

	/**
	 * Set any search settings from the URL
	 */

	const url = new URL(window.location.href);
	const queryID = url.searchParams.get('queryID');
	if (queryID) {
		algoliaSettings.queryID = queryID;
	}
	const objectID = url.searchParams.get('objectID');
	if (objectID) {
		algoliaSettings.objectID = objectID;
	}
	const source = url.searchParams.get('source');
	if (objectID) {
		algoliaSettings.source = source;
	}

	algoliaSettings.hasInitializedTracking = true;

}

setupTracking();

export default class AlgoliaTracking {

	private static indexName = searchAlgoliaSettings('indexName');
	private static currency = 'GBP';

	public static clickedObjectID(productGuid: string, position: number = 1, eventName?: string, queryIDOverride?: string) {
		if (!productGuid) {
			return;
		}

		const queryID = typeof queryIDOverride !== "undefined" && queryIDOverride !== null ? queryIDOverride : AlgoliaTracking.queryID();

		if (queryID) {
			aa('clickedObjectIDsAfterSearch', {
				index: AlgoliaTracking.indexName,
				eventName: eventName ? eventName : 'Product Clicked After Search',
				queryID: queryID,
				objectIDs: [productGuid],
				positions: [position]
			});
		} else {
			aa('clickedObjectIDs', {
				index: AlgoliaTracking.indexName,
				eventName: eventName ? eventName : 'Product Clicked',
				objectIDs: [productGuid]
			});
		}
	}

	public static viewedObjectIDs(productGuids: string[], eventName?: string) {

		if (!productGuids || productGuids.length === 0 || !productGuids[0]) {
			return;
		}

		aa('viewedObjectIDs', {
			index: AlgoliaTracking.indexName,
			eventName: eventName ? eventName : 'Product Viewed',
			objectIDs: productGuids
		});
	}

	public static clickedFilters(eventName: string, filters: string[]) {
		aa('clickedFilters', {
			index: AlgoliaTracking.indexName,
			eventName: eventName,
			filters: filters
		});
	}

	public static viewedFilters(eventName: string, filters: string[]) {
		aa('viewedFilters', {
			index: AlgoliaTracking.indexName,
			eventName: eventName,
			filters: filters
		});
	}

	public static convertedFilters(eventName: string, filters: string[]) {
		aa('convertedFilters', {
			index: AlgoliaTracking.indexName,
			eventName: eventName,
			filters: filters
		});
	}

	public static addedToCart(productGuids: string[], productData: any[], value: number) {
		const queryID = AlgoliaTracking.queryID();
		const objectID = AlgoliaTracking.objectID();
		if (queryID && objectID && productGuids.indexOf(objectID) > -1) {
			aa('addedToCartObjectIDsAfterSearch', {
				eventName: algoliaSettings.source ? `${algoliaSettings.source}: Add To Cart From Search` : 'Add To Cart From Search',
				index: AlgoliaTracking.indexName,
				queryID: AlgoliaTracking.queryID(),
				objectIDs: productGuids,
				objectData: productData,
				value: value,
				currency: AlgoliaTracking.currency
			});

			AlgoliaTracking.trackConvertedFiltersIfAvailable();


		} else {
			aa('addedToCartObjectIDs', {
				eventName: algoliaSettings.source ? `${algoliaSettings.source}: Add To Cart` : 'Add To Cart',
				index: AlgoliaTracking.indexName,
				objectIDs: productGuids,
				objectData: productData,
				value: value,
				currency: AlgoliaTracking.currency
			});
		}
	}

	public static trackConvertedFiltersIfAvailable() {
		const storageKey = 'appliedFilters';
		if (!sessionStorage.getItem(storageKey)) {
			return;
		}
		try {
			const filters = JSON.parse(sessionStorage.getItem(storageKey));
			AlgoliaTracking.convertedFilters('Converted Filters', filters);
			sessionStorage.removeItem(storageKey);
		} catch (e) { }
	}

	public static convertedObjectIDs(eventName: string, productGuids: string[]) {
		const queryID = AlgoliaTracking.queryID();
		if (queryID) {
			aa('convertedObjectIDsAfterSearch', {
				eventName: eventName,
				index: AlgoliaTracking.indexName,
				queryID: AlgoliaTracking.queryID(),
				objectIDs: productGuids
			});
		} else {
			aa('convertedObjectIDs', {
				eventName: eventName,
				index: AlgoliaTracking.indexName,
				objectIDs: productGuids,
			});
		}
	}

	public static purchasedObjectIDs(orderData: IOrderData) {
		if (orderData.anyItemPurchasedAfterSearch) {
			aa('purchasedObjectIDsAfterSearch', {
				eventName: 'Purchase After Search',
				index: AlgoliaTracking.indexName,
				objectIDs: orderData.objectIDs,
				objectData: orderData.objectData,
				value: orderData.value,
				currency: orderData.currency
			});
		} else {
			aa('purchasedObjectIDs', {
				eventName: 'Purchase',
				index: AlgoliaTracking.indexName,
				objectIDs: orderData.objectIDs,
				objectData: orderData.objectData,
				value: orderData.value,
				currency: orderData.currency
			});
		}
	}

	public static queryID() {
		return searchAlgoliaSettings('queryID');
	}

	public static objectID() {
		return searchAlgoliaSettings('objectID');
	}

	public static addAlgoliaQueryToURL(currentUrl: string, guid: string, source?: string, queryIDOverride?: string) {
		const queryID = typeof queryIDOverride !== "undefined" && queryIDOverride !== null ? queryIDOverride : AlgoliaTracking.queryID();
		if (!queryID) {
			return currentUrl;
		}
		try {
			let updatedUrl = new URL(currentUrl, window.location.origin);
			updatedUrl.searchParams.set('queryID', queryID);
			updatedUrl.searchParams.set('objectID', guid);
			if (source) {
				updatedUrl.searchParams.set('source', source);
			}
			return updatedUrl.toString();
		} catch (e) {
			return currentUrl;
		}
	}

	public static updateAlgoliaFromSearchResponse(attributes: {
		QueryID?: string,
		AbTestID?: string,
		AbTestVariantID?: string
	}) {
		if (!attributes) {
			return;
		}
		if (attributes?.QueryID) {
			algoliaSettings.queryID = attributes.QueryID;
		}
		if (attributes.AbTestID) {
			algoliaSettings.AbTestID = attributes.AbTestID;
			dataLayer.push({
				'algoliaAbTestID': attributes.AbTestID
			});
		}
		if (attributes.AbTestVariantID) {
			algoliaSettings.AbTestVariantID = attributes.AbTestVariantID;
			dataLayer.push({
				'algoliaAbTestVariantID': attributes.AbTestVariantID
			});
		}
	}

	public static isSearchPage() {
		return window.location.pathname.toLowerCase().indexOf('/search') > -1;
	}

	public static cleanEventName(name: string) {
		return encodeURI(name).replace(/%20/g, " ").trim();
	}

}

// Page load tracking

if (searchAlgoliaSettings("orderData")) {
	AlgoliaTracking.purchasedObjectIDs(algoliaSettings.orderData);
	algoliaSettings.orderData = null;
}

if (document.querySelector('[data-algolia-product-view]') && !searchAlgoliaSettings("hasTrackedProductPageView")) {
	AlgoliaTracking.viewedObjectIDs([document.querySelector('[data-algolia-product-view]').getAttribute('data-algolia-product-view')], 'PDP: Product Viewed');
	algoliaSettings.hasTrackedProductPageView = true;
}
